/**
 * Ein modaler Dialog — auf dem `<dialog>`-Element, nicht daneben.
 *
 * Die Versuchung ist ein `<div>` mit `position: fixed` und ein bisschen
 * JavaScript. Das sieht nach zwanzig Zeilen aus und ist in Wahrheit eine
 * Liste von Dingen, die einzeln vergessen werden:
 *
 *   * der Fokus muss **hinein**, wenn der Dialog aufgeht,
 *   * er darf nicht heraus, solange er offen ist (die Fokusfalle),
 *   * Escape muss schließen,
 *   * der Fokus muss **zurück** auf das auslösende Element,
 *   * der Hintergrund darf für Vorlesewerkzeuge nicht mehr existieren,
 *   * und die Seite dahinter darf nicht mitscrollen.
 *
 * `showModal()` erledigt alle sechs, und zwar im Browser statt in unserem
 * Code. Das ist kein Bequemlichkeitsargument: Jede dieser Eigenschaften ist
 * eine, die man ohne Vorlesewerkzeug oder ohne Tastatur nicht bemerkt — und
 * die deshalb genau dann fehlt, wenn sie gebraucht wird. `07` §7.14 verlangt
 * Bedienbarkeit im Feld; ein Dialog, aus dem der Tabulator herausläuft, ist
 * mit der Tastatur nicht mehr zu schließen.
 *
 * **Der Preis ist die Verfügbarkeit.** `<dialog>` gibt es in Chrome, Safari
 * und Firefox seit 2022; ein Baustellentablet mit älterem Android sieht
 * stattdessen einen nicht-modalen Kasten — sichtbar und bedienbar, nur ohne
 * Fokusfalle. Das ist der richtige Rückfall: Die Funktion bleibt, die
 * Bequemlichkeit geht.
 *
 * **Was dieses Bauteil nicht tut:** den Inhalt kennen. Es rahmt, es beschriftet
 * und es schließt. Was darin passiert, gehört dem Aufrufer — ein Dialog, der
 * auch noch weiß, welche Felder er zeigt, wäre beim zweiten Anwendungsfall
 * eine Fallunterscheidung und beim dritten eine Bibliothek.
 */

import { useEffect, useRef, type ReactNode } from "react";

export interface DialogEigenschaften {
  readonly offen: boolean;
  /**
   * Was beim Schließen geschieht — Escape, Klick auf den Hintergrund, Knopf.
   *
   * **Pflicht**, und der Grund ist die Escape-Taste: Der Browser schließt das
   * Element von sich aus, ob der Aufrufer davon weiß oder nicht. Ohne diese
   * Rückmeldung stünde `offen` danach auf `true`, während nichts mehr zu sehen
   * ist — und der nächste Öffnungsversuch täte nichts.
   */
  readonly beiSchliessen: () => void;
  /** Überschrift. Pflicht: Ein Dialog ohne Namen ist für ein Vorlesewerkzeug ein Kasten. */
  readonly titel: string;
  readonly children: ReactNode;
}

export function Dialog({ offen, beiSchliessen, titel, children }: DialogEigenschaften) {
  const element = useRef<HTMLDialogElement | null>(null);

  useEffect(() => {
    const d = element.current;
    if (!d) return;
    if (offen && !d.open) {
      // `showModal` statt `show`: Nur die modale Form bringt Fokusfalle,
      // Hintergrundschicht und den `inert`-Zustand dahinter mit.
      if (typeof d.showModal === "function") d.showModal();
      else d.setAttribute("open", "");
    } else if (!offen && d.open) {
      d.close();
    }
  }, [offen]);

  // Escape schließt im Browser, nicht in unserem Code — und meldet es über
  // dieses Ereignis zurück. Ohne die Meldung liefe der Zustand auseinander.
  useEffect(() => {
    const d = element.current;
    if (!d) return;
    const beiClose = () => beiSchliessen();
    d.addEventListener("close", beiClose);
    return () => d.removeEventListener("close", beiClose);
  }, [beiSchliessen]);

  return (
    <dialog
      ref={element}
      className="dialog"
      aria-label={titel}
      // Ein Klick auf die Hintergrundschicht schließt. Geprüft wird auf das
      // Element selbst: Die Schicht **ist** das `<dialog>`, alles Sichtbare
      // liegt im `<div>` darin. Ohne diese Unterscheidung schlösse jeder Klick
      // im Dialog ihn mit.
      onClick={(e) => {
        if (e.target === element.current) beiSchliessen();
      }}
    >
      <div className="dialog__inhalt">
        <div className="dialog__kopf">
          <h2 className="dialog__titel">{titel}</h2>
          <button
            type="button"
            className="dialog__schliessen"
            onClick={beiSchliessen}
          >
            {/* Text statt Kreuz: `07` §7.14 verlangt kurze, eindeutige Texte.
                Ein „×" ist für ein Vorlesewerkzeug ein Multiplikationszeichen. */}
            Schließen
          </button>
        </div>
        {children}
      </div>
    </dialog>
  );
}
